/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Landing page component for email address verification.
 * Reads the `verificationId` query parameter from the URL and calls the
 * `AccessRequestManagementService.verifyEmail$()` method to confirm the
 * applicant's email address.
 *
 * Features:
 * - Extracts the `verificationId` token from the URL query parameters on init.
 * - Triggers the verification API call and transitions between three view
 *   states: `loading`, `success`, and `error`.
 * - Displays a localised success or error message to guide the applicant.
 * - Provides a link to the login page for post-verification navigation.
 */

import {Component, OnDestroy, OnInit, inject} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subject, takeUntil} from 'rxjs';

import {AccessRequestManagementService} from '../../../services/access-request-management.service';

/** Possible view states for the email verification landing page. */
type VerificationState = 'loading' | 'success' | 'error';

@Component({
  selector: 'app-verify-email',
  templateUrl: './verify-email.component.html',
  styleUrl: './verify-email.component.scss',
  standalone: false,
})
export class VerifyEmailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly accessRequestService = inject(
    AccessRequestManagementService,
  );
  private readonly destroy$ = new Subject<void>();

  state: VerificationState = 'loading';

  ngOnInit(): void {
    const requestId = this.route.snapshot.queryParamMap.get('id');
    const organizationId =
      this.route.snapshot.queryParamMap.get('organizationId');

    if (!requestId || !organizationId) {
      this.state = 'error';
      return;
    }

    this.accessRequestService
      .verifyEmail$(organizationId, requestId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.state = 'success';
        },
        error: () => {
          this.state = 'error';
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
